import React from "react";
import { Input } from "@/components/ui/input";
import { Search, XCircle, ChevronUp, ChevronDown } from "lucide-react";
import { SelectAllBox, RowBox } from "@/components/BulkSelect";

export function ListToolbar({ title, search, onSearch, placeholder, filtersOpen, onToggleFilters, onClear, action }) {
  return (
    <div className="flex items-center gap-3 sm:gap-5 flex-wrap" data-testid="list-toolbar">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <div className="ml-auto sm:hidden">{action}</div>
      <div className="relative w-full sm:w-80 order-3 sm:order-none">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input data-testid="list-search-input" placeholder={placeholder} value={search} onChange={(e) => onSearch(e.target.value)} className="pl-9 bg-white" />
      </div>
      <button type="button" onClick={onToggleFilters} className="flex items-center gap-1 text-sm font-medium text-slate-700 order-4 sm:order-none" data-testid="toggle-filters-button">
        Filters {filtersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      <button type="button" onClick={onClear} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 order-5 sm:order-none" data-testid="clear-filters-button">
        <XCircle className="h-4 w-4 text-slate-400" /> Clear Filters
      </button>
      <div className="ml-auto hidden sm:block">{action}</div>
    </div>
  );
}

export function FilterBox({ title, children }) {
  return (
    <div className="rounded border border-slate-300 bg-white px-4 py-3 w-full sm:w-auto sm:min-w-[260px]">
      <div className="text-sm font-medium text-slate-700 mb-2">{title}</div>
      {children}
    </div>
  );
}

export function RadioGroup({ name, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      {options.map((o) => (
        <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-${name}-${o.value}`}>
          <input type="radio" name={name} checked={value === o.value} onChange={() => onChange(o.value)} className="accent-[#2d7bd0] h-4 w-4" />
          {o.label}
        </label>
      ))}
    </div>
  );
}

export function DataTable({ columns, rows, rowKey, emptyText = "No results.", testId, selection }) {
  const [first, ...rest] = columns;
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-b border-slate-300 text-left text-slate-600">
              {selection && <th className="py-2.5 px-2 w-8"><SelectAllBox selection={selection} /></th>}
              {columns.map((c) => <th key={c.key} className={`py-2.5 px-2 font-medium ${c.className || ""}`}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length + (selection ? 1 : 0)} className="py-12 text-center text-slate-400">{emptyText}</td></tr>
            ) : rows.map((r) => (
              <tr key={rowKey(r)} className={`hover:bg-slate-50 ${selection?.selected.has(rowKey(r)) ? "bg-[#eef5fc]" : ""}`} data-testid={`row-${rowKey(r)}`}>
                {selection && <td className="py-3 px-2"><RowBox selection={selection} id={rowKey(r)} /></td>}
                {columns.map((c) => <td key={c.key} className={`py-3 px-2 align-top ${c.className || ""}`}>{c.render(r)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-2" data-testid={testId ? `${testId}-cards` : undefined}>
        {rows.length === 0 ? <div className="py-12 text-center text-slate-400 text-sm">{emptyText}</div> : rows.map((r) => (
          <div key={rowKey(r)} className={`bg-white border border-slate-200 rounded-lg p-3 ${selection?.selected.has(rowKey(r)) ? "ring-2 ring-[#2d7bd0]/40" : ""}`} data-testid={`card-${rowKey(r)}`}>
            <div className="flex items-start gap-3">
              {selection && <div className="pt-1"><RowBox selection={selection} id={rowKey(r)} /></div>}
              <div className="flex-1 min-w-0 text-sm">{first.render(r)}</div>
            </div>
            <dl className="mt-2 grid grid-cols-[90px_1fr] gap-x-2 gap-y-1 text-xs">
              {rest.filter((c) => c.label).map((c) => <React.Fragment key={c.key}><dt className="text-slate-500">{c.label}</dt><dd className="text-slate-800 min-w-0 break-words">{c.render(r)}</dd></React.Fragment>)}
            </dl>
            {rest.filter((c) => !c.label).map((c) => <div key={c.key} className="mt-2 pt-2 border-t border-slate-100">{c.render(r)}</div>)}
          </div>
        ))}
      </div>
    </>
  );
}

export function exportCsv(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
